import React, { useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { Modal as AntdModal } from 'antd';
import Typography, { Variant } from '@lib/components/Typography/Typography';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import {
  IconName,
  ModalIconColor,
  ModalMode,
} from '@lib/components/Modal/enums';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import ModalBody from './ModalBody';
import styles from './Modal.module.scss';

interface Props {
  actions?: React.ReactElement;
  fixedActions?: React.ReactElement;
  caption?: string | null;
  children?: React.ReactElement;
  description?: React.ReactNode;
  descriptionAlign?: 'left' | 'center';
  descriptionChildren?: React.ReactNode;
  icon?: IconName;
  iconColor?: ModalIconColor;
  isOpen: boolean;
  modalBodyClassName?: string;
  modalContentClassName?: string;
  mode?: ModalMode;
  onClose?: () => void;
  title?: React.ReactNode;
  titleAlign?: 'left' | 'center';
  titleVariant?: Variant;
  renderAfterCaption?: React.ReactNode;
  // When true, modal content is unmounted on close.
  // Defaults to true to preserve existing behavior across the app.
  destroyOnHidden?: boolean;
}

function Modal(props: Props) {
  const {
    actions,
    fixedActions,
    caption,
    renderAfterCaption,
    children,
    description,
    descriptionAlign = 'center',
    descriptionChildren,
    icon,
    iconColor = ModalIconColor.Blue,
    isOpen,
    modalContentClassName,
    modalBodyClassName,
    onClose,
    title,
    titleAlign = 'center',
    titleVariant = 'h2',
    mode = ModalMode.Default,
    destroyOnHidden = true,
  } = props;

  const modalWrapRef = useRef<HTMLDivElement>(null);

  // Whether something inside the dialog already deliberately holds focus
  // (e.g. a form field rendered with `autoFocus`). Used to avoid stealing
  // focus away from it below.
  const hasIntentionalFocus = () => {
    const wrap = modalWrapRef.current;
    const active = document.activeElement;
    return !!wrap && !!active && active !== wrap && wrap.contains(active);
  };

  // antd v6 adds a focus trap that, on open, focuses the first focusable
  // element in the dialog — which is our close (X) button. Focus the modal
  // container instead, unless content already claimed focus itself (e.g. a
  // field with `autoFocus`) — otherwise we'd steal it right back and fire a
  // spurious blur. Running this in a layout effect (before the trap's
  // passive effect) means the trap sees focus already inside the dialog and
  // leaves it there, so the close button never receives focus.
  useLayoutEffect(() => {
    if (isOpen && !hasIntentionalFocus()) {
      modalWrapRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    const { body } = document;
    let scrollY = 0;

    if (isOpen) {
      scrollY = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
    }

    return () => {
      if (body.style.position === 'fixed') {
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        window.scrollTo(0, scrollY);
      }
    };
  }, [isOpen]);

  let isCloseBtnVisible = false;
  if (onClose) isCloseBtnVisible = true;

  // Fallback for the focus redirect above: if the portal mounts the content
  // after the layout effect ran (ref was still null), move focus to the
  // container once the open transition completes — again, only if content
  // hasn't already claimed focus itself.
  const afterOpenModal = (open: boolean) => {
    if (open && !hasIntentionalFocus()) {
      modalWrapRef.current?.focus({ preventScroll: true });
    }
  };

  const onRequestClose = () => {
    if (onClose) onClose();
  };

  const classNamesObj = {
    mask: styles.mask,
  };

  // Use default portal container (document.body) so @rc-component/portal
  // can apply its built-in scroll locker correctly.
  const getContainer = () => document.body;

  // Only the currently-open modal should handle Escape. Registering the
  // shortcut for mounted-but-closed modals (the common `<Modal isOpen={...} />`
  // pattern) lets a hidden modal swallow the key via stopImmediatePropagation,
  // so Escape never reaches the visible modal (or antd's native handler).
  useArrowKeyNavigation(
    onClose && isOpen
      ? [
          {
            keys: 'Escape',
            handler: onRequestClose,
          },
        ]
      : [],
  );

  return (
    <AntdModal
      afterOpenChange={afterOpenModal}
      centered
      classNames={classNamesObj}
      closeIcon={null}
      destroyOnHidden={destroyOnHidden}
      footer={null}
      getContainer={getContainer}
      mask={{ closable: false }}
      onCancel={onRequestClose}
      open={isOpen}
      rootClassName={classNames(`mode-${mode}`)}
      width={mode === ModalMode.Default ? 680 : '100%'}
    >
      <div
        ref={modalWrapRef}
        tabIndex={-1}
        className={styles.modalWrap}
        data-test-id="modal__overlay"
      >
        <div className={styles.modalHeader}>
          {caption && (
            <div className={styles.modalHeaderRow}>
              <Typography
                variant="body"
                strong
                className={styles.modalHeaderTitle}
                data-test-id="modal__title"
              >
                {caption}
              </Typography>
              {renderAfterCaption}
            </div>
          )}
          {isCloseBtnVisible && (
            <IconButton
              icon="close"
              type={IconButtonTypes.PrimaryOutlined}
              onClick={onRequestClose}
              className={styles.modalHeaderLeftIcon}
              data-test-id="modal__close-button"
            />
          )}
        </div>
        <div className={styles.modalScrollWrap}>
          <ModalBody
            icon={icon}
            iconColor={iconColor}
            title={title}
            titleAlign={titleAlign}
            titleVariant={titleVariant}
            description={description}
            descriptionAlign={descriptionAlign}
            descriptionChildren={descriptionChildren}
            actions={actions}
            modalBodyClassName={modalBodyClassName}
            modalContentClassName={modalContentClassName}
          >
            {children}
          </ModalBody>
        </div>
        {fixedActions !== undefined && (
          <div className={styles.modalFixedActions}>{fixedActions}</div>
        )}
      </div>
    </AntdModal>
  );
}

export default Modal;
